import { reactive } from 'vue'

type DialogMode = 'confirm' | 'notify' | 'checkbox'

interface DialogState {
  open: boolean
  mode: DialogMode
  icon: string
  title: string
  text: string
  html: string
  confirmColor: string
  cancelColor: string
  confirmButtonText: string
  cancelButtonText: string
  showCancelButton: boolean
  closeable: boolean
  direction: string
  timer: number
  inputPlaceholder: string
  checkboxValue: boolean
}

const state = reactive<DialogState>({
  open: false,
  mode: 'confirm',
  icon: '',
  title: '',
  text: '',
  html: '',
  confirmColor: '#2196F3',
  cancelColor: '#f53535',
  confirmButtonText: 'OK',
  cancelButtonText: 'Cancel',
  showCancelButton: true,
  closeable: true,
  direction: '',
  timer: 1500,
  inputPlaceholder: '',
  checkboxValue: true,
})

let resolvePromise: ((value: any) => void) | null = null

function resetState() {
  state.open = false
  state.mode = 'confirm'
  state.icon = ''
  state.title = ''
  state.text = ''
  state.html = ''
  state.confirmColor = '#2196F3'
  state.cancelColor = '#f53535'
  state.confirmButtonText = 'OK'
  state.cancelButtonText = 'Cancel'
  state.showCancelButton = true
  state.closeable = true
  state.direction = ''
  state.timer = 1500
  state.inputPlaceholder = ''
  state.checkboxValue = true
}

export function confirmDialog(options: {
  icon?: string
  title?: string
  text?: string
  html?: string
  confirmColor?: string
  cancelColor?: string
  confirmButtonText?: string
  cancelButtonText?: string
  showCancelButton?: boolean
  closeable?: boolean
  direction?: string
}): Promise<{ isConfirmed: boolean; value?: any }> {
  resetState()
  Object.assign(state, options, { mode: 'confirm', open: true })
  return new Promise(resolve => {
    resolvePromise = resolve
  })
}

export function notifyDialog(options: {
  icon?: string
  title?: string
  text?: string
  timer?: number
  direction?: string
}): Promise<void> {
  resetState()
  Object.assign(state, options, { mode: 'notify', open: true })
  return new Promise(resolve => {
    resolvePromise = resolve
  })
}

export function checkboxDialog(options: {
  icon?: string
  title?: string
  text?: string
  html?: string
  inputPlaceholder?: string
  confirmButtonText?: string
}): Promise<{ isConfirmed: boolean; value: boolean }> {
  resetState()
  Object.assign(state, options, {
    mode: 'checkbox',
    open: true,
    showCancelButton: false,
    checkboxValue: true,
  })
  return new Promise(resolve => {
    resolvePromise = resolve
  })
}

export function resolveDialog(result: any) {
  if (resolvePromise) {
    resolvePromise(result)
    resolvePromise = null
  }
  state.open = false
}

export function useConfirmDialog() {
  return {
    state,
    confirmDialog,
    notifyDialog,
    checkboxDialog,
    resolveDialog,
  }
}
